// "Loadable" = status in {available, reserved} AND currently in a slot.
// Reserved items appear on purpose so operators can load them for the
// matching customer — the trip loader only warns, it doesn't block.

import React, { useMemo, useState } from 'react';
import {
  Card,
  ListGroup,
  Badge,
  Form,
  Spinner,
  Alert,
  InputGroup,
} from 'react-bootstrap';

import { useItems } from '../../features/warehouse/useItems.js';
import {
  typeBadgeColor,
  matchesTypeFilter,
  matchesTextSearch,
  bySerialAsc,
} from '../../features/warehouse/warehouseUtils.js';

export default function LoadableItemsPanel({ onPick, busy }) {
  const { data: items, isLoading, error } = useItems();
  const [filterType, setFilterType] = useState('all');
  const [search, setSearch] = useState('');

  const loadable = useMemo(() => {
    if (!items) return [];
    return items
      .filter(it => {
        if (it.status !== 'available' && it.status !== 'reserved') return false;
        if (it.location?.kind !== 'slot') return false;
        return matchesTypeFilter(it, filterType) && matchesTextSearch(it, search);
      })
      .sort(bySerialAsc);
  }, [items, filterType, search]);

  return (
    <Card className="h-100">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <strong>Available to load</strong>
        <Badge bg="secondary">{loadable.length}</Badge>
      </Card.Header>

      <Card.Body className="p-2" style={{ maxHeight: '72vh', overflowY: 'auto' }}>
        <div className="d-flex gap-1 mb-2">
          <Form.Select
            size="sm"
            style={{ flex: 1 }}
            value={filterType}
            onChange={e => setFilterType(e.target.value)}
          >
            <option value="all">All types</option>
            <option value="drum">Drums</option>
            <option value="europallet">Europallets</option>
            <option value="coil">Coils</option>
          </Form.Select>
        </div>

        <InputGroup size="sm" className="mb-2">
          <Form.Control
            type="text"
            placeholder="search serial / cable…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </InputGroup>

        {isLoading && (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" />
          </div>
        )}

        {error && (
          <Alert variant="danger" className="small">
            {error.message}
          </Alert>
        )}

        {!isLoading && loadable.length === 0 && (
          <div className="text-muted small text-center py-3">
            Nothing available.
          </div>
        )}

        <ListGroup variant="flush">
          {loadable.map(item => (
            <ListGroup.Item
              key={item._id}
              action
              disabled={busy}
              onClick={() => onPick?.(item.serial)}
              className="px-2 py-2"
            >
              <div className="d-flex justify-content-between align-items-start">
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="fw-bold small text-truncate">{item.serial}</div>
                  <div className="small text-muted text-truncate">
                    {item.cableName} · {item.lengthMeters}m · {item.weightKg || 0}kg
                  </div>
                </div>
                <div className="text-end">
                  <Badge bg={typeBadgeColor(item.type)}>{item.type}</Badge>
                  {item.status === 'reserved' && (
                    <div className="small text-primary mt-1">reserved</div>
                  )}
                </div>
              </div>
            </ListGroup.Item>
          ))}
        </ListGroup>
      </Card.Body>
    </Card>
  );
}

